import { Component, Inject, ViewChild, ElementRef, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { OrganizationLocation } from 'app/modules/organization/models/organization-location';
import { LocationDistanceRate, DistanceRateMatrix } from '../../models/location-distance-rate.model';
import { LocationRate } from '../../models/location-rate.interface';
import { LocationOption } from '../../models/location-option.interface';
import { SetNotificationAction } from '@core/store/notification/notification.actions';
import { AlertDialogType } from '@shared/models';
import { Store } from '@ngxs/store';
import { DecimalPipe } from '@angular/common';

@Component({
    selector: 'hmt-location-rates-dialog',
    templateUrl: './location-rates-dialog.component.html',
    styleUrls: ['./location-rates-dialog.component.scss'],
    providers: [DecimalPipe],
    standalone: false
})
export class LocationRatesDialogComponent implements OnInit, OnDestroy {
  @ViewChild('ratesTable') ratesTable: ElementRef;
  @ViewChild('virtualViewport') virtualScrollViewport: CdkVirtualScrollViewport;
  locationColumns: { locationId: string }[] = [];
  locationRates: LocationRate[] = [];
  locationForms: FormGroup[] = [];
  locations: LocationOption[] = [];
  filteredLocationsMap: Map<number, LocationOption[]> = new Map();
  locationForm: FormGroup;
  isLocationSet = false;
  tempDistances: { [key: string]: string } = {};
  tempRates: { [key: string]: string } = {};
  clipboardColumnData: { type: 'distance' | 'rate' | 'location'; values: string[] } | null = null;
  isLoading = false;
  loadingMessage = '';

  private showingLocationNames = true;
  private locationMap: Map<string, string> = new Map();
  private pasteTimeoutId: any = null;
  private isProcessing = false;

  constructor(
    public dialogRef: MatDialogRef<LocationRatesDialogComponent>,
    private store$: Store,
    @Inject(MAT_DIALOG_DATA) public data: {
      savedMatrix?: DistanceRateMatrix;
      locations: OrganizationLocation[];
      distanceRateMatrix?: DistanceRateMatrix;
    },
    private formBuilder: FormBuilder,
    private decimalPipe: DecimalPipe,
    private cdr: ChangeDetectorRef
  ) {
    this.locationForm = new FormGroup({
      location: new FormControl('')
    });
  }

  ngOnInit() {
    this.locations = this.data.locations.map(loc => ({
      locationId: loc.referenceId,
      locationName: loc.locationName
    }));

    // Create one-way mapping from referenceId to locationName only
    // Avoid bidirectional mapping to prevent conflicts when multiple IDs have same name
    this.locations.forEach(loc => {
      this.locationMap.set(loc.locationId, loc.locationName);
    });
    if (this.data.distanceRateMatrix && Object.keys(this.data.distanceRateMatrix).length > 0) {
      this.initializeFromSavedMatrix(this.data.distanceRateMatrix);
    } else {
      this.locationColumns = [{ locationId: '' }];
      this.locationRates = [{ location: '', distances: [null], rates: [null] }];
      this.initializeLocationForms();
      this.addInitialRow();
    }
    this.filteredLocationsMap.set(0, [...this.locations]);
    this.initializeTempValues();
  }

  ngOnDestroy() {
    // Clear any pending timeouts to prevent memory leaks
    if (this.pasteTimeoutId) {
      clearTimeout(this.pasteTimeoutId);
      this.pasteTimeoutId = null;
    }

    // Reset processing flags
    this.isProcessing = false;
    this.isLoading = false;
  }

  copyColumn(columnIndex: number, type: 'distance' | 'rate' | 'location') {
    if (type === 'location') {
      const locationData = this.locationRates.map(rate => rate.location || '');
      const locationText = locationData.join('\n');
      navigator.clipboard.writeText(locationText);
      this.clipboardColumnData = {
        type,
        values: locationData
      };
      this.store$.dispatch(
        new SetNotificationAction(
          'Location column copied to clipboard',
          AlertDialogType.SUCCESS
        )
      );
      return;
    }
    const columnData = this.locationRates.map(rate => {
      const value = type === 'distance' ? rate.distances[columnIndex] : rate.rates[columnIndex];
      return value !== null ? this.formatNumber(value) : '';
    });
    const textData = columnData.join('\n');
    navigator.clipboard.writeText(textData);
    this.clipboardColumnData = {
      type,
      values: columnData
    };
    this.store$.dispatch(
      new SetNotificationAction(
        `${type === 'distance' ? 'Distance' : 'Rate'} column copied to clipboard`,
        AlertDialogType.SUCCESS
      )
    );
  }

  pasteColumn(columnIndex: number, type: 'distance' | 'rate' | 'location') {
    navigator.clipboard.readText().then(pastedText => {
      if (pastedText) {
        const values = pastedText.split(/[\n\r]+/).map(v => v.trim()).filter(v => v);
        if (values.length > 0) {
          this.processColumnPasteValues(values, columnIndex, type);
          return;
        }
      }
      if (!this.clipboardColumnData || this.clipboardColumnData.type !== type) {
        this.store$.dispatch(
          new SetNotificationAction(
            `No ${type} column data available in clipboard. Copy a ${type} column first.`,
            AlertDialogType.WARNING
          )
        );
        return;
      }
      this.processColumnPasteValues(this.clipboardColumnData.values, columnIndex, type);
    }).catch(err => {
      if (this.clipboardColumnData && this.clipboardColumnData.type === type) {
        this.processColumnPasteValues(this.clipboardColumnData.values, columnIndex, type);
      } else {
        this.store$.dispatch(
          new SetNotificationAction(
            `Failed to read clipboard: ${err.message}`,
            AlertDialogType.ERROR
          )
        );
      }
    });
  }

  onColumnPaste(event: ClipboardEvent, columnIndex: number, type: 'distance' | 'rate' | 'location') {
    event.preventDefault();
    const clipboardData = event.clipboardData;
    const pastedText = clipboardData.getData('text').trim();
    if (!pastedText) {
      return;
    }
    const values = pastedText.split(/[\n\r]+/).map(v => v.trim()).filter(v => v);
    this.processColumnPasteValues(values, columnIndex, type);
  }

  processColumnPasteValues(values: string[], columnIndex: number, type: 'distance' | 'rate' | 'location') {
    const isLargeDataset = values.length > 500;
    let hasErrors = false;

    // Show loading for large datasets
    if (isLargeDataset && !this.isLoading) {
      this.isLoading = true;
      this.loadingMessage = `Processing ${values.length} ${type} values...`;
    }

    if (type === 'location') {
      // Track first occurrences and identify duplicates to clear
      const seenLocations = new Set<string>();
      const duplicatesToClear = new Set<number>(); // Track indices to clear
      const duplicateNames = new Set<string>();

      // Pre-process all values for efficient duplicate detection
      for (let i = 0; i < values.length; i++) {
        const value = values[i];
        const normalizedKey = this.normalizeLocationKey(value);

        if (seenLocations.has(normalizedKey)) {
          // This is a duplicate - mark for clearing
          duplicatesToClear.add(i);
          duplicateNames.add(value);
        } else {
          seenLocations.add(normalizedKey);
        }
      }

      // Show warning for duplicates that will be cleared
      if (duplicateNames.size > 0) {
        const duplicateList = Array.from(duplicateNames).slice(0, 10); // Limit displayed duplicates
        const moreText = duplicateNames.size > 10 ? ` and ${duplicateNames.size - 10} more` : '';

        this.store$.dispatch(
          new SetNotificationAction(
            `Duplicate locations in pasted data: ${duplicateList.join(', ')}${moreText}. Each location must be unique.`,
            AlertDialogType.WARNING
          )
        );
      }

      // Clear duplicate values but continue processing
      for (const index of duplicatesToClear) {
        values[index] = ''; // Clear the duplicate
      }
    } else {
      // Optimize number validation for large datasets
      const invalidValues = [];
      for (let i = 0; i < Math.min(values.length, 100); i++) { // Check first 100 values for performance
        if (this.parseNumber(values[i]) === null) {
          invalidValues.push(values[i]);
          if (invalidValues.length >= 5) { break; } // Limit error reporting
        }
      }

      if (invalidValues.length > 0) {
        this.store$.dispatch(
          new SetNotificationAction(
            `Invalid ${type} values: "${invalidValues.join('", "')}"${invalidValues.length >= 5 ? ' and more' : ''}. Must be numbers.`,
            AlertDialogType.WARNING
          )
        );
        hasErrors = true;
      }
    }

    // Always process location types (duplicates have been cleared), only skip for invalid numbers
    if (!hasErrors || type === 'location') {
      // Process in batches for large datasets
      if (isLargeDataset) {
        this.processColumnValuesBatched(values, columnIndex, type);
      } else {
        this.processColumnValuesImmediate(values, columnIndex, type);
      }
    } else {
      // Hide loading if there were errors
      if (this.isLoading) {
        this.isLoading = false;
        this.loadingMessage = '';
      }
    }
  }

  private processColumnValuesImmediate(values: string[], columnIndex: number, type: 'distance' | 'rate' | 'location') {
    // Ensure we have enough rows
    while (values.length > this.locationRates.length) {
      this.addRow();
    }

    if (type === 'location') {
      for (let rowIndex = 0; rowIndex < values.length; rowIndex++) {
        // Process all values (duplicates were already cleared in parent method)
        if (values[rowIndex]) {
          this.locationRates[rowIndex].location = values[rowIndex];

          // Find and set the referenceId
          const locationObj = this.data.locations.find(loc =>
            loc.locationName === values[rowIndex] || loc.referenceId === values[rowIndex]
          );

          if (locationObj) {
            this.locationRates[rowIndex].referenceId = locationObj.referenceId;
            this.locationRates[rowIndex].location = locationObj.locationName;
          } else {
            this.locationRates[rowIndex].referenceId = values[rowIndex];
          }

          this.onLocationRowUpdate(rowIndex);
        } else {
          // Clear the location if the value was emptied due to duplicate
          this.locationRates[rowIndex].location = '';
          this.locationRates[rowIndex].referenceId = undefined;
        }
      }
    } else {
      // Process distance/rate values
      for (let rowIndex = 0; rowIndex < values.length; rowIndex++) {
        this.processColumnValue(values[rowIndex], rowIndex, columnIndex, type);
      }
    }

    this.showColumnPasteSuccess(type);
  }

  private processColumnValuesBatched(values: string[], columnIndex: number, type: 'distance' | 'rate' | 'location') {
    const batchSize = 100;
    let currentIndex = 0;

    // Ensure we have enough rows upfront
    while (values.length > this.locationRates.length) {
      this.addRow();
    }

    const processBatch = () => {
      const endIndex = Math.min(currentIndex + batchSize, values.length);

      if (type === 'location') {
        for (let rowIndex = currentIndex; rowIndex < endIndex; rowIndex++) {
          // Process all values (duplicates were already cleared in parent method)
          if (values[rowIndex]) {
            this.locationRates[rowIndex].location = values[rowIndex];

            // Find and set the referenceId
            const locationObj = this.data.locations.find(loc =>
              loc.locationName === values[rowIndex] || loc.referenceId === values[rowIndex]
            );

            if (locationObj) {
              this.locationRates[rowIndex].referenceId = locationObj.referenceId;
              this.locationRates[rowIndex].location = locationObj.locationName;
            } else {
              this.locationRates[rowIndex].referenceId = values[rowIndex];
            }

            this.onLocationRowUpdate(rowIndex);
          } else {
            // Clear the location if the value was emptied due to duplicate
            this.locationRates[rowIndex].location = '';
            this.locationRates[rowIndex].referenceId = undefined;
          }
        }
      } else {
        for (let rowIndex = currentIndex; rowIndex < endIndex; rowIndex++) {
          this.processColumnValue(values[rowIndex], rowIndex, columnIndex, type);
        }
      }

      currentIndex = endIndex;

      // Update progress
      if (this.isLoading) {
        const progress = Math.round((currentIndex / values.length) * 100);
        this.loadingMessage = `Processing ${type} values... ${progress}%`;
      }

      if (currentIndex < values.length) {
        // Continue with next batch
        setTimeout(processBatch, 1);
      } else {
        // Finished processing
        this.finalizeColumnPaste(type);
      }
    };

    // Start batch processing
    setTimeout(processBatch, 10);
  }

  private processColumnValue(value: string, rowIndex: number, columnIndex: number, type: 'distance' | 'rate') {
    const rowLocation = this.locationRates[rowIndex]?.location;
    const colLocationId = this.locationColumns[columnIndex]?.locationId;
    const rowLocationId = this.locationMap.get(rowLocation) || rowLocation;

    // Handle same location case (should be 0)
    if (rowLocation && colLocationId && (rowLocationId === colLocationId)) {
      this.locationRates[rowIndex][type === 'distance' ? 'distances' : 'rates'][columnIndex] = 0;
      this.setTempKey(rowIndex, columnIndex, type, '0.000');
      return;
    }

    const numValue = this.parseNumber(value);
    if (numValue !== null) {
      this.locationRates[rowIndex][type === 'distance' ? 'distances' : 'rates'][columnIndex] = numValue;
      this.setTempKey(rowIndex, columnIndex, type, this.formatNumber(numValue));
    }
  }

  private finalizeColumnPaste(type: 'distance' | 'rate' | 'location') {
    // Force array reference change
    this.locationRates = [...this.locationRates];

    // Update virtual scroll
    requestAnimationFrame(() => {
      this.refreshVirtualScrollViewport();

      // Hide loading and show success
      setTimeout(() => {
        this.isLoading = false;
        this.loadingMessage = '';
        this.showColumnPasteSuccess(type);
      }, 50);
    });
  }

  private showColumnPasteSuccess(type: 'distance' | 'rate' | 'location') {
    this.store$.dispatch(
      new SetNotificationAction(
        `${type === 'location' ? 'Location' : type === 'distance' ? 'Distance' : 'Rate'} column pasted successfully`,
        AlertDialogType.SUCCESS
      )
    );
  }

  initializeLocationForms() {
    this.locationForms = this.locationColumns.map(() =>
      this.formBuilder.group({
        location: ['']
      })
    );
  }

  getLocationForm(index: number): FormGroup {
    return this.locationForms[index];
  }

  addColumn() {
    const newColumnIndex = this.locationColumns.length;
    this.locationColumns.push({ locationId: '' });
    this.addLocationForm();
    this.locationRates.forEach((rate, rowIndex) => {
      rate.distances.push(null);
      rate.rates.push(null);
      this.setTempKey(rowIndex, newColumnIndex, 'distance', '');
      this.setTempKey(rowIndex, newColumnIndex, 'rate', '');
    });
    this.filteredLocationsMap.set(newColumnIndex, [...this.locations]);
  }

  removeColumn(index: number) {
    if (this.locationColumns.length > 1) {
      this.locationColumns.splice(index, 1);
      this.locationForms.splice(index, 1);
      this.locationRates.forEach(rate => {
        rate.distances.splice(index, 1);
        rate.rates.splice(index, 1);
      });
      this.filteredLocationsMap.delete(index);
      const newMap = new Map<number, LocationOption[]>();
      this.locationColumns.forEach((_, i) => {
        const locations = this.filteredLocationsMap.get(i + (i >= index ? 1 : 0));
        newMap.set(i, locations || [...this.locations]);
      });
      this.filteredLocationsMap = newMap;
    }
  }

  addRow() {
    const newRow: LocationRate = {
      location: '',
      referenceId: undefined, // Will be set when location is selected
      distances: new Array(this.locationColumns.length).fill(null),
      rates: new Array(this.locationColumns.length).fill(null)
    };
    this.locationRates.push(newRow);

    const rowIndex = this.locationRates.length - 1;
    newRow.distances.forEach((_, colIndex) => {
      this.setTempKey(rowIndex, colIndex, 'distance', '');
    });
    newRow.rates.forEach((_, colIndex) => {
      this.setTempKey(rowIndex, colIndex, 'rate', '');
    });

    // Update virtual scroll viewport to display the new row
    this.refreshVirtualScrollViewport();
  }

  removeRow(index: number) {
    if (index >= 0 && index < this.locationRates.length) {
      // Clean up temp data for the removed row
      const rowToRemove = this.locationRates[index];
      if (rowToRemove) {
        // Clean up temp keys for this row
        rowToRemove.distances.forEach((_, colIndex) => {
          const distanceKey = this.getTempKey(index, colIndex, 'distance');
          const rateKey = this.getTempKey(index, colIndex, 'rate');
          delete this.tempDistances[distanceKey];
          delete this.tempRates[rateKey];
        });
      }

      this.locationRates.splice(index, 1);

      // Reindex remaining temp keys
      this.reindexTempKeys();

      this.refreshVirtualScrollViewport();
    }
  }

  private reindexTempKeys() {
    // Clean up and reindex temp keys after row removal
    const newTempDistances: { [key: string]: string } = {};
    const newTempRates: { [key: string]: string } = {};

    this.locationRates.forEach((rate, rowIndex) => {
      rate.distances.forEach((distance, colIndex) => {
        const newDistanceKey = this.getTempKey(rowIndex, colIndex, 'distance');
        const newRateKey = this.getTempKey(rowIndex, colIndex, 'rate');

        // Set temp values based on actual data values with proper formatting
        if (distance !== null) {
          newTempDistances[newDistanceKey] = this.formatNumber(distance);
        } else {
          newTempDistances[newDistanceKey] = '';
        }
      });

      rate.rates.forEach((rateValue, colIndex) => {
        const newRateKey = this.getTempKey(rowIndex, colIndex, 'rate');

        // Set temp values based on actual data values with proper formatting
        if (rateValue !== null) {
          newTempRates[newRateKey] = this.formatNumber(rateValue);
        } else {
          newTempRates[newRateKey] = '';
        }
      });
    });

    this.tempDistances = newTempDistances;
    this.tempRates = newTempRates;
  }

  onLocationSearch(event: any, columnIndex: number) {
    const searchValue = event.target.value.toLowerCase();
    if (!searchValue) {
      this.filteredLocationsMap.set(columnIndex, [...this.locations]);
      return;
    }
    const filtered = this.locations.filter(location =>
      location.locationName.toLowerCase().includes(searchValue)
    );
    this.filteredLocationsMap.set(columnIndex, filtered);
  }

  getFilteredLocations(columnIndex: number): LocationOption[] {
    const baseLocations = this.filteredLocationsMap.get(columnIndex) || [...this.locations];
    const alreadySelectedLocationIds = this.locationColumns
      .map((col, idx) => idx !== columnIndex && col.locationId ? col.locationId : null)
      .filter(id => id !== null);
    const uniqueLocationIds = new Set<string>();
    return baseLocations.filter(location => {
      if (alreadySelectedLocationIds.includes(location.locationId)) {
        return false;
      }
      if (uniqueLocationIds.has(location.locationId)) {
        return false;
      }
      uniqueLocationIds.add(location.locationId);
      return true;
    });
  }

  updateLocation(columnIndex: number) {
    const form = this.locationForms[columnIndex];
    const selectedLocation = form.get('location').value as LocationOption;
    if (selectedLocation && selectedLocation.locationId) {
      const isDuplicate = this.locationColumns.some(
        (col, idx) => idx !== columnIndex && col.locationId === selectedLocation.locationId
      );
      if (isDuplicate) {
        form.get('location').setValue('');
        this.store$.dispatch(
          new SetNotificationAction(
            'This location has already been selected in another column',
            AlertDialogType.WARNING
          )
        );
        return;
      }
      this.locationColumns[columnIndex].locationId = selectedLocation.locationId;
      this.isLocationSet = true;
      this.locationRates.forEach((rate, rowIndex) => {
        const rowLocationId = this.locationMap.get(rate.location) || rate.location;
        if (rowLocationId === selectedLocation.locationId) {
          rate.distances[columnIndex] = 0;
          rate.rates[columnIndex] = 0;
          this.setTempKey(rowIndex, columnIndex, 'distance', '0.000');
          this.setTempKey(rowIndex, columnIndex, 'rate', '0.000');
        }
      });
    }
  }

  isLocationSetForColumn(columnIndex: number): boolean {
    const form = this.locationForms[columnIndex];
    return form && form.get('location').value;
  }

  displayLocationName(location: LocationOption): string {
    return location ? location.locationName : '';
  }

  onRefresh() {
    // Show loading for large datasets
    const isLargeDataset = this.locationRates.length > 500;

    if (isLargeDataset) {
      this.isLoading = true;
      this.loadingMessage = `Refreshing ${this.locationRates.length} locations...`;
    }

    this.showingLocationNames = !this.showingLocationNames;

    // For large datasets, process in batches to avoid blocking UI
    if (isLargeDataset) {
      this.processRefreshInBatches();
    } else {
      this.processRefreshImmediate();
    }
  }

  private processRefreshImmediate() {
    // Update the data with new locations using stored referenceIds
    const updatedRates = this.locationRates.map(rate => {
      if (!rate.location) {
        return rate;
      }

      let newLocation = '';

      if (this.showingLocationNames) {
        // Currently showing IDs, switch to names
        if (rate.referenceId) {
          // Use the stored referenceId to find the location name
          const locationObj = this.data.locations.find(loc => loc.referenceId === rate.referenceId);
          newLocation = locationObj ? locationObj.locationName : rate.referenceId;
        } else {
          // Fallback: Use locationMap
          const locationName = this.locationMap.get(rate.location);
          newLocation = locationName || rate.location;
        }
      } else {
        // Currently showing names, switch to IDs
        if (rate.referenceId) {
          // Use the stored referenceId
          newLocation = rate.referenceId;
        } else {
          // Fallback: Find referenceId from location name
          const locationObj = this.data.locations.find(loc => loc.locationName === rate.location);
          newLocation = locationObj ? locationObj.referenceId : rate.location;
        }
      }

      return {
        ...rate,
        location: newLocation
      };
    });

    // Set the new array reference
    this.locationRates = updatedRates;

    // Update virtual scroll
    setTimeout(() => {
      if (this.virtualScrollViewport) {
        this.virtualScrollViewport.checkViewportSize();
        this.cdr.detectChanges();
      }
    }, 0);
  }

  private processRefreshInBatches() {
    const batchSize = 100;
    const totalItems = this.locationRates.length;
    let currentIndex = 0;

    const processBatch = () => {
      const endIndex = Math.min(currentIndex + batchSize, totalItems);

      // Process current batch
      for (let i = currentIndex; i < endIndex; i++) {
        const rate = this.locationRates[i];
        if (rate.location) {
          let newLocation = '';

          if (this.showingLocationNames) {
            // Currently showing IDs, switch to names
            if (rate.referenceId) {
              // Use the stored referenceId to find the location name
              const locationObj = this.data.locations.find(loc => loc.referenceId === rate.referenceId);
              newLocation = locationObj ? locationObj.locationName : rate.referenceId;
            } else {
              // Fallback: Use locationMap
              const locationName = this.locationMap.get(rate.location);
              newLocation = locationName || rate.location;
            }
          } else {
            // Currently showing names, switch to IDs
            if (rate.referenceId) {
              // Use the stored referenceId
              newLocation = rate.referenceId;
            } else {
              // Fallback: Find referenceId from location name
              const locationObj = this.data.locations.find(loc => loc.locationName === rate.location);
              newLocation = locationObj ? locationObj.referenceId : rate.location;
            }
          }

          rate.location = newLocation;
        }
      }

      currentIndex = endIndex;

      // Update progress
      if (this.isLoading) {
        const progress = Math.round((currentIndex / totalItems) * 100);
        this.loadingMessage = `Refreshing locations... ${progress}%`;
      }

      // Continue processing or finish
      if (currentIndex < totalItems) {
        // Continue with next batch
        setTimeout(processBatch, 1);
      } else {
        // Finished - update virtual scroll
        this.finishRefresh();
      }
    };

    // Start batch processing
    setTimeout(processBatch, 10);
  }

  private finishRefresh() {
    // Force new array reference for virtual scroll
    this.locationRates = [...this.locationRates];

    // Hide loading
    this.isLoading = false;
    this.loadingMessage = '';

    // Update virtual scroll with longer delay for large datasets
    setTimeout(() => {
      if (this.virtualScrollViewport) {
        try {
          this.virtualScrollViewport.checkViewportSize();
          this.cdr.detectChanges();

          // Additional update for large datasets
          requestAnimationFrame(() => {
            if (this.virtualScrollViewport) {
              this.cdr.detectChanges();
            }
          });
        } catch (error) {
          console.warn('Virtual scroll refresh error:', error);
        }
      }
    }, 50);
  }

  copyToClipboard() {
    const headers = ['Location'];
    this.locationColumns.forEach(() => {
      headers.push('Distance', 'Rate');
    });
    const rows = [headers.join('\t')];
    this.locationRates.forEach(rate => {
      const row = [rate.location];
      for (let i = 0; i < this.locationColumns.length; i++) {
        row.push(
          rate.distances[i] !== null ? this.formatNumber(rate.distances[i]) : '',
          rate.rates[i] !== null ? this.formatNumber(rate.rates[i]) : ''
        );
      }
      rows.push(row.join('\t'));
    });
    const tableText = rows.join('\n');
    navigator.clipboard.writeText(tableText);
  }

  isLocationDuplicateInRows(location: string, currentRowIndex?: number): boolean {
    if (!location) { return false; }

    const normalizedKey = this.normalizeLocationKey(location);

    return this.locationRates.some((rate, index) =>
      index !== currentRowIndex &&
      rate.location &&
      this.normalizeLocationKey(rate.location) === normalizedKey
    );
  }

  // Helper method to check duplicates against existing data only
  private isLocationDuplicateInExisting(location: string): boolean {
    if (!location) { return false; }

    const normalizedKey = this.normalizeLocationKey(location);

    return this.locationRates.some(rate =>
      rate.location &&
      this.normalizeLocationKey(rate.location) === normalizedKey
    );
  }

  // Helper method to normalize location keys for consistent comparison
  private normalizeLocationKey(location: string): string {
    if (!location) { return ''; }

    // Try to get the mapped value (could be ID->Name or Name->ID)
    const mappedValue = this.locationMap.get(location);

    if (mappedValue) {
      // If we found a mapping, determine which is the ID
      // Check if the original location is in our locations array as an ID
      const isLocationId = this.locations.some(loc => loc.locationId === location);

      if (isLocationId) {
        // Original location is an ID, use it as the normalized key
        return location;
      } else {
        // Original location is a name, use the mapped ID as the normalized key
        return mappedValue;
      }
    }

    // No mapping found, use the location as-is
    return location;
  }

  // Special duplicate checking for large datasets to avoid false positives
  private isLocationDuplicateInLargeDataset(location: string, currentRowIndex: number, pastedLocationsSet: Set<string>): boolean {
    if (!location) { return false; }

    const locationKey = this.normalizeLocationKey(location);

    // First check against existing data (before paste operation) - only check rows that existed before paste
    for (let i = 0; i < this.locationRates.length; i++) {
      const existingLocation = this.locationRates[i]?.location;
      if (existingLocation && i !== currentRowIndex) {
        const existingKey = this.normalizeLocationKey(existingLocation);
        if (existingKey === locationKey) {
          return true;
        }
      }
    }

    // For large datasets, use more efficient tracking of processed locations in current paste operation
    const processedLocations = new Set<string>();

    // Check if this location appears multiple times in the pasted data up to current row
    for (let i = 0; i < currentRowIndex && i < this.locationRates.length; i++) {
      const existingLocation = this.locationRates[i]?.location;
      if (existingLocation) {
        const existingKey = this.normalizeLocationKey(existingLocation);
        if (existingKey === locationKey && processedLocations.has(existingKey)) {
          return true;
        }
        processedLocations.add(existingKey);
      }
    }

    return false;
  }

  onPaste(event: ClipboardEvent) {
    if (event.target instanceof HTMLInputElement) {
      return;
    }

    // Prevent multiple simultaneous paste operations
    if (this.isProcessing) {
      return;
    }

    event.preventDefault();

    // Clear any existing timeout
    if (this.pasteTimeoutId) {
      clearTimeout(this.pasteTimeoutId);
      this.pasteTimeoutId = null;
    }

    const clipboardData = event.clipboardData;
    const pastedText = clipboardData.getData('text');
    const rows = pastedText.trim().split(/[\n\r]+/);

    const isFirstRowHeader = rows.length > 0 &&
      rows[0].toLowerCase().includes('location') &&
      (rows[0].toLowerCase().includes('distance') || rows[0].toLowerCase().includes('rate'));

    const dataRows = isFirstRowHeader ? rows.slice(1) : rows;

    // Show loading for all datasets, but use async processing for large ones
    const isLargeDataset = dataRows.length > 100;
    const showAsyncProcessing = dataRows.length > 50; // Show loading for 50+ rows

    if (showAsyncProcessing) {
      this.isProcessing = true;
      this.isLoading = true;
      this.loadingMessage = `Processing ${dataRows.length} rows...`;
    }
    if (isLargeDataset) {
      // Use setTimeout for very large datasets to prevent UI blocking
      this.pasteTimeoutId = setTimeout(() => {
        this.processPasteData(dataRows);
        this.pasteTimeoutId = null;
      }, 10);
    } else {
      // Process immediately but still show loading for medium datasets
      setTimeout(() => this.processPasteData(dataRows), 1);
    }
  }

  private processPasteData(dataRows: string[]) {
    const isLargeDataset = dataRows.length > 100;

    // Pre-process to handle duplicates - keep first occurrence, clear subsequent ones
    const processedLocations = new Set<string>();
    const duplicateNames = new Set<string>();
    const clearedDuplicateIndices = new Set<number>();

    // First pass: identify duplicates and mark them for clearing
    dataRows.forEach((row, index) => {
      const cells = row.split(/\t+/).map(cell => cell.trim()).filter(v => v);
      if (cells.length > 0 && cells[0]) {
        const location = cells[0];
        const normalizedKey = this.normalizeLocationKey(location);

        if (processedLocations.has(normalizedKey)) {
          // This is a duplicate - mark for clearing
          duplicateNames.add(location);
          clearedDuplicateIndices.add(index);
        } else {
          processedLocations.add(normalizedKey);
        }
      }
    });

    // Show warning for duplicates that will be cleared
    if (duplicateNames.size > 0) {
      const duplicateList = Array.from(duplicateNames).slice(0, 10);
      const moreText = duplicateNames.size > 10 ? ` and ${duplicateNames.size - 10} more` : '';

      this.store$.dispatch(
        new SetNotificationAction(
          `Duplicate locations found: ${duplicateList.join(', ')}${moreText}.`,
          AlertDialogType.WARNING
        )
      );
    }

    // Process each row of pasted data
    let validRowsProcessed = 0;

    for (let index = 0; index < dataRows.length; index++) {
      const row = dataRows[index];
      const cells = row.split(/\t+/).map(cell => cell.trim()).filter(v => v);

      // Skip empty rows
      if (cells.length === 0 || (cells.length === 1 && !cells[0])) {
        continue;
      }

      const rowIndex = validRowsProcessed;

      // Ensure we have enough rows
      while (rowIndex >= this.locationRates.length) {
        this.addRow();
      }

      const currentRow = this.locationRates[rowIndex];

      // Process location (first cell)
      if (cells[0]) {
        if (clearedDuplicateIndices.has(index)) {
          // This row has a duplicate location - clear it but process the row
          currentRow.location = '';
          currentRow.referenceId = undefined;
        } else {
          currentRow.location = cells[0];
          // Try to find the referenceId for this location
          const locationObj = this.data.locations.find(loc =>
            loc.locationName === cells[0] || loc.referenceId === cells[0]
          );
          currentRow.referenceId = locationObj ? locationObj.referenceId : cells[0];
        }
      }

      // Process distance and rate columns
      let columnIndex = 0;
      for (let i = 1; i < cells.length; i += 2) {
        // Ensure we have enough columns
        while (columnIndex >= this.locationColumns.length) {
          this.addColumn();
        }

        const rowLocationId = this.locationMap.get(currentRow.location) || currentRow.location;
        const colLocationId = this.locationColumns[columnIndex]?.locationId;

        // Handle same location case (distance and rate should be 0)
        if (rowLocationId && colLocationId && rowLocationId === colLocationId) {
          currentRow.distances[columnIndex] = 0;
          currentRow.rates[columnIndex] = 0;
          this.setTempKey(rowIndex, columnIndex, 'distance', '0.000');
          this.setTempKey(rowIndex, columnIndex, 'rate', '0.000');
        } else {
          // Process distance value
          if (cells[i]) {
            const distance = this.parseNumber(cells[i]);
            currentRow.distances[columnIndex] = distance;
            this.setTempKey(rowIndex, columnIndex, 'distance',
              distance !== null ? this.formatNumber(distance) : '');
          } else {
            currentRow.distances[columnIndex] = null;
            this.setTempKey(rowIndex, columnIndex, 'distance', '');
          }

          // Process rate value
          if (cells[i + 1]) {
            const rate = this.parseNumber(cells[i + 1]);
            currentRow.rates[columnIndex] = rate;
            this.setTempKey(rowIndex, columnIndex, 'rate',
              rate !== null ? this.formatNumber(rate) : '');
          } else {
            currentRow.rates[columnIndex] = null;
            this.setTempKey(rowIndex, columnIndex, 'rate', '');
          }
        }
        columnIndex++;
      }

      validRowsProcessed++;
    }

    // Update virtual scroll and finish processing
    this.finalizePasteOperation(validRowsProcessed, isLargeDataset);
  }

  private finalizePasteOperation(processedRows: number, isLargeDataset: boolean) {
    // Force array reference change to trigger change detection
    this.locationRates = [...this.locationRates];

    if (this.isLoading) {
      this.loadingMessage = 'Finalizing table updates...';
    }

    // Use different update strategies based on dataset size
    if (isLargeDataset) {
      // For large datasets, use multiple animation frames for smoother updates
      requestAnimationFrame(() => {
        this.refreshVirtualScrollViewport();

        requestAnimationFrame(() => {
          // Final cleanup
          this.finishPasteProcessing(processedRows);
        });
      });
    } else {
      // For smaller datasets, use immediate update
      setTimeout(() => {
        this.refreshVirtualScrollViewport();
        this.finishPasteProcessing(processedRows);
      }, 10);
    }
  }

  private finishPasteProcessing(processedRows: number) {
    // Hide loading state
    this.isLoading = false;
    this.loadingMessage = '';
    this.isProcessing = false;

    // Show success message
    this.store$.dispatch(
      new SetNotificationAction(
        `Successfully processed ${processedRows} location entries.`,
        AlertDialogType.SUCCESS
      )
    );
  }

  onCellPaste(event: ClipboardEvent, type: 'location' | 'distance' | 'rate', rowIndex: number, colIndex?: number) {
    event.preventDefault();
    const clipboardData = event.clipboardData;
    const pastedText = clipboardData.getData('text').trim();

    if (!pastedText) {
      return;
    }

    if (type === 'location') {
      // Allow duplicate location names, set the referenceId for proper tracking
      this.locationRates[rowIndex].location = pastedText;

      // Find and set the referenceId
      const locationObj = this.data.locations.find(loc =>
        loc.locationName === pastedText || loc.referenceId === pastedText
      );

      if (locationObj) {
        this.locationRates[rowIndex].referenceId = locationObj.referenceId;
        this.locationRates[rowIndex].location = locationObj.locationName;
      } else {
        this.locationRates[rowIndex].referenceId = pastedText;
      }

      this.onLocationRowUpdate(rowIndex);
    } else if (type === 'distance' && colIndex !== undefined) {
      const numericValue = this.parseNumber(pastedText);
      if (numericValue !== null) {
        if (this.isDuplicateDistance(numericValue, rowIndex, colIndex)) {
          this.store$.dispatch(
            new SetNotificationAction(
              `Duplicate distance ${this.formatNumber(numericValue)} in column ${colIndex + 1}`,
              AlertDialogType.WARNING
            )
          );
          return;
        }

        this.locationRates[rowIndex].distances[colIndex] = numericValue;
        const formattedValue = this.formatNumber(numericValue);
        this.setTempKey(rowIndex, colIndex, 'distance', formattedValue);

        if (event.target instanceof HTMLInputElement) {
          event.target.value = formattedValue;
        }
      }
    } else if (type === 'rate' && colIndex !== undefined) {
      const numericValue = this.parseNumber(pastedText);
      if (numericValue !== null) {
        this.locationRates[rowIndex].rates[colIndex] = numericValue;
        const formattedValue = this.formatNumber(numericValue);
        this.setTempKey(rowIndex, colIndex, 'rate', formattedValue);

        if (event.target instanceof HTMLInputElement) {
          event.target.value = formattedValue;
        }
      }
    }
  }

  validateLocationColumns(): { isValid: boolean; message: string } {
    for (let i = 0; i < this.locationColumns.length; i++) {
      const form = this.locationForms[i];
      const selectedLocation = form.get('location').value as LocationOption;
      if (!selectedLocation || !selectedLocation.locationId) {
        return {
          isValid: false,
          message: `Please select a location for column ${i + 1}`
        };
      }
    }
    return { isValid: true, message: '' };
  }

  validateLocationRows(): { isValid: boolean; message: string } {
    if (this.locationRates.length === 0) {
      return {
        isValid: false,
        message: 'Please add at least one row'
      };
    }

    // Count rows with actual location data (skip cleared duplicates)
    const rowsWithLocations = this.locationRates.filter(row => row.location && row.location.trim());

    if (rowsWithLocations.length === 0) {
      return {
        isValid: false,
        message: 'Please add at least one valid location'
      };
    }

    for (let i = 0; i < this.locationRates.length; i++) {
      const row = this.locationRates[i];

      // Skip validation for empty locations (cleared duplicates)
      if (!row.location || !row.location.trim()) {
        continue;
      }

      if (!this.isValidLocation(row.location)) {
        return {
          isValid: false,
          message: `Invalid location "${row.location}" in row ${i + 1}. Please enter a valid location.`
        };
      }

      // Check for duplicate referenceIds (not location names) - referenceIds must be unique
      if (row.referenceId) {
        const duplicateRefId = this.locationRates.some((otherRow, otherIndex) =>
          otherIndex !== i && otherRow.referenceId === row.referenceId
        );

        if (duplicateRefId) {
          return {
            isValid: false,
            message: `Duplicate reference ID "${row.referenceId}" in row ${i + 1}. Each reference ID must be unique.`
          };
        }
      }

      // Only validate distance/rate for rows that have locations
      for (let j = 0; j < this.locationColumns.length; j++) {
        if (row.distances[j] === null || row.distances[j] === undefined) {
          return {
            isValid: false,
            message: `Please enter distance for row ${i + 1}, column ${j + 1}`
          };
        }
        if (row.rates[j] === null || row.rates[j] === undefined) {
          return {
            isValid: false,
            message: `Please enter rate for row ${i + 1}, column ${j + 1}`
          };
        }
        if (this.isDuplicateDistance(row.distances[j], i, j)) {
          return {
            isValid: false,
            message: `Duplicate distance ${this.formatNumber(row.distances[j])} in column ${j + 1}`
          };
        }
      }
    }
    return { isValid: true, message: '' };
  }

  onSave() {
    const columnValidation = this.validateLocationColumns();
    if (!columnValidation.isValid) {
        this.store$.dispatch(
        new SetNotificationAction(
          columnValidation.message,
          AlertDialogType.ERROR,
        ),
      );
      return;
    }

    const rowValidation = this.validateLocationRows();
    if (!rowValidation.isValid) {
      this.store$.dispatch(
        new SetNotificationAction(
          rowValidation.message,
          AlertDialogType.ERROR,
        ),
      );
      return;
    }

    const matrix: DistanceRateMatrix = {};

    this.locationColumns.forEach((column, colIndex) => {
      const form = this.locationForms[colIndex];
      const selectedLocation = form.get('location').value as LocationOption;
      const columnLocationId = selectedLocation.locationId;

      const distanceRates: LocationDistanceRate[] = this.locationRates
        .filter(row => row.location && row.location.trim() && row.distances[colIndex] !== null && row.rates[colIndex] !== null)
        .map(row => {
          // Use the stored referenceId if available, otherwise fallback to lookup logic
          let consistentLocationId: string;

          if (row.referenceId) {
            // Use the internally stored referenceId
            consistentLocationId = row.referenceId;
          } else {
            // Fallback: Find the location object by matching locationName
            const location = this.data.locations.find(loc =>
              loc.locationName === row.location || loc.referenceId === row.location
            );

            if (!location) {
              // If not found directly, try using locationMap to find the mapped value
              const mappedValue = this.locationMap.get(row.location);
              if (mappedValue) {
                const foundLocation = this.data.locations.find(loc =>
                  loc.referenceId === mappedValue || loc.locationName === mappedValue
                );
                consistentLocationId = foundLocation?.referenceId || row.location;
              } else {
                consistentLocationId = row.location;
              }
            } else {
              consistentLocationId = location.referenceId;
            }
          }

          return {
            locationId: consistentLocationId,
            locationRefId: consistentLocationId,
            distance: row.distances[colIndex],
            rate: row.rates[colIndex]
          };
        });

      if (distanceRates.length > 0) {
        matrix[columnLocationId] = distanceRates;
      }
    });
    this.dialogRef.close(matrix);
  }

  onCancel() {
    this.dialogRef.close();
  }

  onClose() {
    this.dialogRef.close();
  }

  formatNumber(value: number): string {
    if (value === null || value === undefined) {
      return '';
    }
    const numStr = value.toFixed(3);
    const [integerPart, decimalPart] = numStr.split('.');
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${formattedInteger}.${decimalPart}`;
  }

  parseNumber(value: string): number {
    if (!value) {
      return null;
    }
    const cleanValue = value.replace(/,/g, '');
    if (!/^\d*\.?\d*$/.test(cleanValue)) {
      return null;
    }
    const parsedValue = Number(cleanValue);
    return isNaN(parsedValue) ? null : parsedValue;
  }

  onRateInput(event: any, rowIndex: number, colIndex: number) {
    const value = event;
    const rowLocation = this.locationRates[rowIndex]?.location;
    const colLocationId = this.locationColumns[colIndex]?.locationId;
    const rowLocationId = this.locationMap.get(rowLocation) || rowLocation;
    if (rowLocation && colLocationId && (rowLocationId === colLocationId)) {
      this.locationRates[rowIndex].rates[colIndex] = 0;
      this.setTempKey(rowIndex, colIndex, 'rate', '0.000');
      return;
    }
    if (value === '') {
      this.locationRates[rowIndex].rates[colIndex] = null;
      this.setTempKey(rowIndex, colIndex, 'rate', '');
    } else {
      const numericValue = this.parseNumber(value);
      if (numericValue !== null) {
        this.locationRates[rowIndex].rates[colIndex] = numericValue;
        this.setTempKey(rowIndex, colIndex, 'rate', value);
      } else {
        this.locationRates[rowIndex].rates[colIndex] = null;
        this.setTempKey(rowIndex, colIndex, 'rate', '');
      }
    }
  }

  onDistanceInput(event: any, rowIndex: number, colIndex: number) {
    const value = event;
    const rowLocation = this.locationRates[rowIndex]?.location;
    const colLocationId = this.locationColumns[colIndex]?.locationId;
    const rowLocationId = this.locationMap.get(rowLocation) || rowLocation;
    if (rowLocation && colLocationId && (rowLocationId === colLocationId)) {
      this.locationRates[rowIndex].distances[colIndex] = 0;
      this.setTempKey(rowIndex, colIndex, 'distance', '0.000');
      return;
    }
    if (value === '') {
      this.locationRates[rowIndex].distances[colIndex] = null;
      this.setTempKey(rowIndex, colIndex, 'distance', '');
    } else {
      const numericValue = this.parseNumber(value);
      if (numericValue !== null) {
        this.locationRates[rowIndex].distances[colIndex] = numericValue;
        this.setTempKey(rowIndex, colIndex, 'distance', value);
      } else {
        this.locationRates[rowIndex].distances[colIndex] = null;
        this.setTempKey(rowIndex, colIndex, 'distance', '');
      }
    }
  }

  onInputBlur(event: any, rowIndex: number, colIndex: number, type: 'rate' | 'distance') {
    const rowLocation = this.locationRates[rowIndex]?.location;
    const colLocationId = this.locationColumns[colIndex]?.locationId;
    const rowLocationId = this.locationMap.get(rowLocation) || rowLocation;
    if (rowLocation && colLocationId && (rowLocationId === colLocationId)) {
      if (type === 'distance' || type === 'rate') {
        this.locationRates[rowIndex][type === 'distance' ? 'distances' : 'rates'][colIndex] = 0;
        this.setTempKey(rowIndex, colIndex, type, '0.000');
        event.target.value = '0.000';
        return;
      }
    }
    const value = event.target.value;
    if (!value) {
      if (type === 'rate') {
        this.locationRates[rowIndex].rates[colIndex] = null;
      } else {
        this.locationRates[rowIndex].distances[colIndex] = null;
      }
      this.setTempKey(rowIndex, colIndex, type, '');
      return;
    }

    const numericValue = this.parseNumber(value);
    if (numericValue !== null) {
      if (type === 'rate') {
        this.locationRates[rowIndex].rates[colIndex] = numericValue;
        this.setTempKey(rowIndex, colIndex, type, this.formatNumber(numericValue));
        event.target.value = this.formatNumber(numericValue);
      } else {
        if (this.isDuplicateDistance(numericValue, rowIndex, colIndex)) {
          this.store$.dispatch(
            new SetNotificationAction(
              `Duplicate distance ${this.formatNumber(numericValue)} in column ${colIndex + 1}`,
              AlertDialogType.WARNING
            )
          );
          this.locationRates[rowIndex].distances[colIndex] = null;
          this.setTempKey(rowIndex, colIndex, 'distance', '');
          event.target.value = '';
          return;
        }
        this.locationRates[rowIndex].distances[colIndex] = numericValue;
        this.setTempKey(rowIndex, colIndex, type, this.formatNumber(numericValue));
        event.target.value = this.formatNumber(numericValue);
      }
    } else {
      if (type === 'rate') {
        this.locationRates[rowIndex].rates[colIndex] = null;
      } else {
        this.locationRates[rowIndex].distances[colIndex] = null;
      }
      this.setTempKey(rowIndex, colIndex, type, '');
      event.target.value = '';
    }
  }

  onLocationInput(location: string, rowIndex: number) {
    // Allow duplicate location names, but set the referenceId for proper tracking
    if (location) {
      // Find the referenceId for this location name
      const locationObj = this.data.locations.find(loc =>
        loc.locationName === location || loc.referenceId === location
      );

      if (locationObj) {
        // Set both display name and internal referenceId
        this.locationRates[rowIndex].location = locationObj.locationName;
        this.locationRates[rowIndex].referenceId = locationObj.referenceId;
      } else {
        // If location not found in data, use as-is (might be manual entry)
        this.locationRates[rowIndex].location = location;
        this.locationRates[rowIndex].referenceId = location;
      }
    }
    return true;
  }

  getTempDistance(rowIndex: number, colIndex: number): string {
    const rowLocation = this.locationRates[rowIndex]?.location;
    const colLocationId = this.locationColumns[colIndex]?.locationId;
    const rowLocationId = this.locationMap.get(rowLocation) || rowLocation;
    if (rowLocation && colLocationId && (rowLocationId === colLocationId)) {
      return '0.000';
    }
    return this.tempDistances[this.getTempKey(rowIndex, colIndex, 'distance')] || '';
  }

  getTempRate(rowIndex: number, colIndex: number): string {
    const rowLocation = this.locationRates[rowIndex]?.location;
    const colLocationId = this.locationColumns[colIndex]?.locationId;
    const rowLocationId = this.locationMap.get(rowLocation) || rowLocation;
    if (rowLocation && colLocationId && (rowLocationId === colLocationId)) {
      return '0.000';
    }
    return this.tempRates[this.getTempKey(rowIndex, colIndex, 'rate')] || '';
  }

  onLocationRowUpdate(rowIndex: number) {
    const row = this.locationRates[rowIndex];
    if (!row || !row.referenceId) {
      return;
    }

    // Use the stored referenceId for comparison
    this.locationColumns.forEach((column, colIndex) => {
      if (column.locationId === row.referenceId) {
        this.locationRates[rowIndex].distances[colIndex] = 0;
        this.locationRates[rowIndex].rates[colIndex] = 0;
        this.setTempKey(rowIndex, colIndex, 'distance', '0.000');
        this.setTempKey(rowIndex, colIndex, 'rate', '0.000');
      }
    });
  }

  updateVirtualScrollViewport() {
    // Simple, lightweight update with debouncing
    if (this.virtualScrollViewport && !this.isProcessing) {
      requestAnimationFrame(() => {
        if (this.virtualScrollViewport) {
          this.cdr.detectChanges();
        }
      });
    }
  }

  refreshVirtualScrollViewport() {
    // Force data reference change for virtual scroll detection
    if (this.virtualScrollViewport) {
      // Create new array reference to trigger change detection
      this.locationRates = [...this.locationRates];

      requestAnimationFrame(() => {
        if (this.virtualScrollViewport) {
          try {
            this.virtualScrollViewport.checkViewportSize();
            this.cdr.detectChanges();

            // Additional frame to ensure rendering is complete
            requestAnimationFrame(() => {
              if (this.virtualScrollViewport) {
                this.cdr.detectChanges();
              }
            });
          } catch (error) {
            console.warn('Virtual scroll update error:', error);
          }
        }
      });
    }
  }

  trackByIndex(index: number, item: any): string {
    // Use a combination of index and item content for stable tracking
    // This handles deletions better than just using index alone
    return `${item?.location || 'empty'}-${index}`;
  }

  private isValidLocation(location: string): boolean {
    if (!location) { return false; }

    // Check if location exists either as ID, name, or is mapped in our locationMap
    return this.locations.some(loc =>
      loc.locationId === location ||
      loc.locationName === location
    ) || this.locationMap.has(location);
  }

  private initializeFromSavedMatrix(matrix: DistanceRateMatrix) {
    // Initialize columns from saved matrix keys (these should be referenceIds)
    this.locationColumns = Object.keys(matrix).map(locationId => ({ locationId }));

    // Initialize location forms with proper LocationOption objects
    this.locationForms = this.locationColumns.map(column => {
      const location = this.locations.find(loc => loc.locationId === column.locationId);
      return this.formBuilder.group({
        location: [location]
      });
    });

    // Collect all unique row location referenceIds from the saved data
    const rowLocationIds = new Set<string>();

    Object.values(matrix).forEach(rates => {
      rates.forEach(rate => {
        // Use the saved locationId as-is since it should already be a referenceId
        // This prevents duplicate entries caused by name/ID mapping confusion
        rowLocationIds.add(rate.locationId);
      });
    });

    // Create location rates preserving all unique referenceIds
    // Store referenceId internally while showing clean display names
    this.locationRates = Array.from(rowLocationIds).map(rowLocationId => {
      // Find the location object using referenceId
      const locationObj = this.data.locations.find(loc => loc.referenceId === rowLocationId);

      // For display, use clean location name (allow duplicates)
      const displayLocation = locationObj ? locationObj.locationName : rowLocationId;

      const distances: number[] = [];
      const rates: number[] = [];

      Object.keys(matrix).forEach((columnLocationId, colIndex) => {
        // Find rate data by directly comparing referenceIds
        const rateData = matrix[columnLocationId].find(r => r.locationId === rowLocationId);

        if (columnLocationId === rowLocationId) {
          distances.push(0);
          rates.push(0);
        } else {
          distances.push(rateData ? rateData.distance : null);
          rates.push(rateData ? rateData.rate : null);
        }
      });

      return {
        location: displayLocation,
        referenceId: rowLocationId, // Store referenceId internally
        distances,
        rates
      };
    });

    if (this.locationRates.length === 0) {
      this.addInitialRow();
    }
  }

  private addLocationForm() {
    const form = this.formBuilder.group({
      location: ['']
    });
    this.locationForms.push(form);
  }

  private addInitialRow() {
    this.addRow();
  }

  private initializeTempValues() {
    this.locationRates.forEach((rate, rowIndex) => {
      rate.distances.forEach((distance, colIndex) => {
        const formattedDistance = distance !== null ? this.formatNumber(distance) : '';
        this.setTempKey(rowIndex, colIndex, 'distance', formattedDistance);
      });
      rate.rates.forEach((rateValue, colIndex) => {
        const formattedRate = rateValue !== null ? this.formatNumber(rateValue) : '';
        this.setTempKey(rowIndex, colIndex, 'rate', formattedRate);
      });
    });
  }

  private getTempKey(rowIndex: number, colIndex: number, type: 'distance' | 'rate'): string {
    return `${rowIndex}-${colIndex}-${type}`;
  }

  private setTempKey(rowIndex: number, colIndex: number, type: 'distance' | 'rate', value: string) {
    const key = this.getTempKey(rowIndex, colIndex, type);
    if (type === 'distance') {
      this.tempDistances[key] = value;
    } else {
      this.tempRates[key] = value;
    }
  }

  // Debug method to verify data consistency (can be called from console for debugging)
  verifyDataConsistency(): void {
    console.log('=== Data Consistency Check ===');

    // Check location mappings
    console.log('Location Map Entries:', Array.from(this.locationMap.entries()));

    // Check available locations
    console.log('Available Locations:', this.locations);

    // Check current UI state
    console.log('Current Columns:', this.locationColumns);
    console.log('Current Rows:', this.locationRates.map((r, i) => ({
      index: i,
      location: r.location,
      distanceCount: r.distances.length,
      rateCount: r.rates.length,
      hasEmptyLocation: !r.location || !r.location.trim()
    })));

    // Check form states
    console.log('Column Forms:', this.locationForms.map((f, i) => ({
      index: i,
      hasValue: !!f.get('location')?.value,
      locationId: f.get('location')?.value?.locationId,
      locationName: f.get('location')?.value?.locationName
    })));
  }

  private isDuplicateDistance(distance: number, currentRowIndex: number, columnIndex: number): boolean {
    if (distance === null || distance === undefined) {
      return false;
    }

    if (distance === 0) {
      const rowLocation = this.locationRates[currentRowIndex]?.location;
      const colLocationId = this.locationColumns[columnIndex]?.locationId;
      const rowLocationId = this.locationMap.get(rowLocation) || rowLocation;

      if (rowLocationId === colLocationId) {
        return false;
      }
    }
    return this.locationRates.some((rate, rowIndex) =>
      rowIndex !== currentRowIndex &&
      rate.distances[columnIndex] !== null &&
      rate.distances[columnIndex] === distance
    );
  }
}

